import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { buildApiUrl } from '../../utils/apiUrl';
import { useEmpresaId } from '../../context/EmpresaContext';
import { supabase } from '../../supabase';
import { enviarReporteWhatsApp } from './enviarReporteWhatsApp';
import { ClipboardList, Plus, Save, RefreshCw, Loader2, CalendarCheck, ChevronDown, ChevronRight, Trash2, X, Printer, MessageCircle } from 'lucide-react';

interface Labor {
  id: number;
  nombre: string;
  tipo: string;
  clase: 'muestreo' | 'tarea';
  etapa_ancla: 'forza' | 'siembra';
  estadio_codigo: string;
  offset_dias: number;
  orden: number;
  activo: boolean;
}
interface Estadio {
  codigo: string;
  descripcion: string;
  varfecha: string;
}

// Etiqueta legible del ancla (varfecha) de cada estadio.
const ANCLA_LABEL: Record<string, string> = {
  fecha_siembra: 'días desde siembra',
  fecha_induccion: 'días desde forza (inducción)',
  fecha_segunda: 'días desde 2.ª forza',
  fecha_semillero: 'días desde semillero',
  fecha_induccion2: 'días desde inducción 2.º ciclo',
};

export function CatalogoLaboresMuestreosPage() {
  const empresaId = useEmpresaId();
  const [labores, setLabores] = useState<Labor[]>([]);
  const [estadios, setEstadios] = useState<Estadio[]>([]);
  const [abiertos, setAbiertos] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [guardando, setGuardando] = useState<number | 'nuevo' | null>(null);
  const [generando, setGenerando] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [edit, setEdit] = useState<Record<number, Partial<Labor>>>({});
  const [ocultarInactivos, setOcultarInactivos] = useState(false);
  // Confirmación de borrado en cascada
  const [borrar, setBorrar] = useState<{ labor: Labor; programados: number; ejecutados: number } | null>(null);
  const [borrando, setBorrando] = useState(false);
  const [nuevo, setNuevo] = useState<Partial<Labor>>({ estadio_codigo: '', tipo: 'muestreo', clase: 'muestreo', offset_dias: 0, activo: true });
  // Reporte PDF / WhatsApp
  const [empresaNombre, setEmpresaNombre] = useState('');
  const [waModal, setWaModal] = useState(false);
  const [waNumero, setWaNumero] = useState('');
  const [waEnviando, setWaEnviando] = useState(false);

  useEffect(() => {
    supabase.from('empresas').select('nombre').eq('id', empresaId).maybeSingle()
      .then(({ data }) => setEmpresaNombre(data?.nombre || ''));
  }, [empresaId]);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(buildApiUrl(`/aplicaciones/labores/catalogo?empresa_id=${empresaId}`));
      const j = await res.json();
      setLabores(j.ok ? (j.labores || []) : []);
      setEdit({});
    } finally {
      setLoading(false);
    }
  }, [empresaId]);

  const cargarEstadios = useCallback(async () => {
    // Vía backend (service_role): app_estadios tiene RLS que la bloquea para anon.
    let est: Estadio[] = [];
    try {
      const res = await fetch(buildApiUrl(`/aplicaciones/labores/estadios?empresa_id=${empresaId}`));
      const j = await res.json();
      est = j.ok ? (j.estadios || []) : [];
    } catch { est = []; }
    setEstadios(est);
    // abrir el primero por defecto + default del form
    if (est.length) {
      setAbiertos((prev) => (prev.size ? prev : new Set([est[0].codigo])));
      setNuevo((n) => (n.estadio_codigo ? n : { ...n, estadio_codigo: est[0].codigo }));
    }
  }, [empresaId]);

  useEffect(() => { cargar(); cargarEstadios(); }, [cargar, cargarEstadios]);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const setCampo = (id: number, campo: keyof Labor, val: any) =>
    setEdit((e) => ({ ...e, [id]: { ...e[id], [campo]: val } }));

  const guardar = async (l: Labor) => {
    const cambios = edit[l.id];
    if (!cambios) return;
    setGuardando(l.id);
    try {
      const res = await fetch(buildApiUrl(`/aplicaciones/labores/catalogo/${l.id}?empresa_id=${empresaId}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cambios),
      });
      const j = await res.json();
      if (!j.ok) { setToast({ msg: j.error || 'Error', ok: false }); return; }
      setToast({ msg: `"${l.nombre}" actualizada`, ok: true });
      cargar();
    } catch (e: any) {
      setToast({ msg: e?.message || 'Error de red', ok: false });
    } finally {
      setGuardando(null);
    }
  };

  // Pedir el conteo de impacto y abrir el modal de confirmación.
  const pedirBorrar = async (l: Labor) => {
    try {
      const res = await fetch(buildApiUrl(`/aplicaciones/labores/catalogo/${l.id}?empresa_id=${empresaId}&dry=1`), { method: 'DELETE' });
      const j = await res.json();
      setBorrar({ labor: l, programados: j.programados || 0, ejecutados: j.ejecutados || 0 });
    } catch (e: any) {
      setToast({ msg: e?.message || 'Error de red', ok: false });
    }
  };

  const confirmarBorrar = async () => {
    if (!borrar) return;
    setBorrando(true);
    try {
      const res = await fetch(buildApiUrl(`/aplicaciones/labores/catalogo/${borrar.labor.id}?empresa_id=${empresaId}`), { method: 'DELETE' });
      const j = await res.json();
      if (!j.ok) { setToast({ msg: j.error || 'Error', ok: false }); return; }
      setToast({ msg: `"${borrar.labor.nombre}" eliminada (${j.programados} muestreos borrados)`, ok: true });
      setBorrar(null);
      cargar();
    } catch (e: any) {
      setToast({ msg: e?.message || 'Error de red', ok: false });
    } finally {
      setBorrando(false);
    }
  };

  const crear = async () => {
    if (!nuevo.nombre?.trim()) { setToast({ msg: 'Falta el nombre', ok: false }); return; }
    if (!nuevo.estadio_codigo) { setToast({ msg: 'Falta el estadio', ok: false }); return; }
    setGuardando('nuevo');
    try {
      const res = await fetch(buildApiUrl(`/aplicaciones/labores/catalogo?empresa_id=${empresaId}`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(nuevo),
      });
      const j = await res.json();
      if (!j.ok) { setToast({ msg: j.error || 'Error', ok: false }); return; }
      setToast({ msg: 'Labor agregada', ok: true });
      setNuevo((n) => ({ estadio_codigo: n.estadio_codigo, tipo: 'muestreo', clase: 'muestreo', offset_dias: 0, activo: true }));
      cargar();
    } catch (e: any) {
      setToast({ msg: e?.message || 'Error de red', ok: false });
    } finally {
      setGuardando(null);
    }
  };

  const regenerar = async () => {
    setGenerando(true);
    try {
      const res = await fetch(buildApiUrl('/aplicaciones/labores/generar'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ empresa_id: empresaId }),
      });
      const j = await res.json();
      setToast({ msg: j.ok ? `Calendario regenerado: ${j.creadas} muestreos` : (j.error || 'Error'), ok: !!j.ok });
    } catch (e: any) {
      setToast({ msg: e?.message || 'Error de red', ok: false });
    } finally {
      setGenerando(false);
    }
  };

  const valFor = (l: Labor, campo: keyof Labor) => (edit[l.id]?.[campo] ?? l[campo]) as any;
  const dirty = (id: number) => !!edit[id] && Object.keys(edit[id]).length > 0;
  const toggle = (cod: string) => setAbiertos((prev) => {
    const next = new Set(prev);
    next.has(cod) ? next.delete(cod) : next.add(cod);
    return next;
  });

  // ── Reporte del catálogo abierto por estadio (calidad ERP) ──────────────────
  // Respeta el check "Ocultar inactivos". Estadios en el mismo orden que la UI.
  const buildReporteHtml = useCallback(() => {
    const esc = (s: any) => String(s ?? '').replace(/[<>&]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[c] as string));
    const hoyStr = new Date().toLocaleDateString('es-CR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

    let totalLab = 0;
    const seccionesHtml = estadios.map((est) => {
      const grupo = labores
        .filter((l) => l.estadio_codigo === est.codigo)
        .filter((l) => !ocultarInactivos || l.activo)
        .sort((a, b) => (Number(a.offset_dias) || 0) - (Number(b.offset_dias) || 0));
      if (grupo.length === 0) return '';
      totalLab += grupo.length;
      const filas = grupo.map((l) => `
        <tr${l.activo ? '' : ' class="inactiva"'}>
          <td>${esc(l.nombre)}</td>
          <td class="col-clase">${l.clase === 'tarea' ? 'Tarea' : 'Muestreo'}</td>
          <td class="col-dias">${esc(l.offset_dias)}</td>
          <td class="col-estado">${l.activo ? 'Activo' : 'Inactivo'}</td>
        </tr>`).join('');
      return `
        <div class="estadio">
          <div class="estadio-head">
            <span class="cod">${esc(est.codigo)}</span>
            <span class="desc">${esc(est.descripcion)}</span>
            <span class="ancla">· ${esc(ANCLA_LABEL[est.varfecha] || est.varfecha)}</span>
            <span class="cnt">${grupo.length} labores</span>
          </div>
          <table class="rep">
            <thead><tr>
              <th style="width:54%">Labor / Muestreo</th>
              <th style="width:16%">Clase</th>
              <th style="width:14%; text-align:right">Días</th>
              <th style="width:16%">Estado</th>
            </tr></thead>
            <tbody>${filas}</tbody>
          </table>
        </div>`;
    }).join('');

    return `<!DOCTYPE html><html lang="es"><head><meta charset="UTF-8">
<title>Catálogo de Muestreos por Estadio</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: 'Segoe UI', Arial, sans-serif; color: #1e293b; margin: 0; padding: 28px 32px; font-size: 12px; }
  .rep-head { display: table; width: 100%; border-bottom: 2px solid #16a34a; padding-bottom: 10px; margin-bottom: 16px; }
  .rep-head .left { display: table-cell; vertical-align: bottom; }
  .rep-head .right { display: table-cell; text-align: right; vertical-align: bottom; color: #64748b; font-size: 10px; }
  .rep-empresa { font-size: 17px; font-weight: 700; color: #0f172a; line-height: 1.1; }
  .rep-sub { font-size: 10px; color: #64748b; margin-top: 1px; }
  .rep-title { font-size: 13px; font-weight: 600; color: #16a34a; margin-top: 3px; }
  .estadio { margin-bottom: 16px; page-break-inside: avoid; }
  .estadio-head { display: flex; align-items: baseline; gap: 8px; padding: 6px 0; border-bottom: 1px solid #cbd5e1; margin-bottom: 4px; }
  .estadio-head .cod { font-size: 14px; font-weight: 800; color: #0f172a; }
  .estadio-head .desc { font-size: 12px; color: #334155; }
  .estadio-head .ancla { font-size: 10px; color: #94a3b8; }
  .estadio-head .cnt { margin-left: auto; font-size: 10px; color: #64748b; background: #f1f5f9; padding: 2px 8px; border-radius: 999px; }
  table.rep { width: 100%; border-collapse: collapse; }
  table.rep thead th { background: #16a34a; color: #fff; text-align: left; padding: 5px 8px; font-size: 10px; text-transform: uppercase; letter-spacing: 0.04em; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  table.rep tbody td { padding: 4px 8px; border-bottom: 1px solid #e2e8f0; }
  table.rep tbody tr:nth-child(even) td { background: #f8fafc; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  tr.inactiva td { color: #94a3b8; font-style: italic; }
  .col-clase { color: #475569; }
  .col-dias { text-align: right; font-family: 'Consolas', monospace; }
  .col-estado { color: #475569; }
  .rep-foot { margin-top: 18px; padding-top: 8px; border-top: 1px solid #e2e8f0; font-size: 9px; color: #94a3b8; text-align: center; }
  @page { margin: 14mm; }
</style></head>
<body>
  <div class="rep-head">
    <div class="left">
      <div class="rep-empresa">${esc(empresaNombre || 'Control de Piña')}</div>
      <div class="rep-sub">Control de Piña · MD2 Golden</div>
      <div class="rep-title">Catálogo de Muestreos por Estadio</div>
    </div>
    <div class="right">${ocultarInactivos ? 'Solo activas' : 'Todas'}<br/>Generado: ${esc(hoyStr)}</div>
  </div>
  ${seccionesHtml || '<p style="color:#94a3b8">Sin labores para mostrar.</p>'}
  <div class="rep-foot">MYA ERP · Control de Piña — Catálogo de Muestreos · ${totalLab} labores</div>
</body></html>`;
  }, [estadios, labores, ocultarInactivos, empresaNombre]);

  const imprimirPdf = () => {
    const html = buildReporteHtml();
    const w = window.open('', '_blank');
    if (!w) { setToast({ msg: 'El navegador bloqueó la ventana de impresión.', ok: false }); return; }
    w.document.write(html); w.document.close();
    w.onload = () => { w.focus(); w.print(); };
  };

  const enviarWhatsApp = async () => {
    if (!waNumero.trim()) { setToast({ msg: 'Ingresá un número de WhatsApp.', ok: false }); return; }
    setWaEnviando(true);
    try {
      const r = await enviarReporteWhatsApp({
        html: buildReporteHtml(),
        numero: waNumero,
        caption: `📋 *Catálogo de Muestreos por Estadio*\n${empresaNombre || 'Control de Piña'}`,
        fileName: 'catalogo-muestreos.jpg',
        empresaId,
      });
      if (r.ok) { setToast({ msg: 'Reporte enviado por WhatsApp ✓', ok: true }); setWaModal(false); }
      else setToast({ msg: r.error || 'No se pudo enviar.', ok: false });
    } finally {
      setWaEnviando(false);
    }
  };

  const inputCls = 'bg-transparent outline-none text-sm';
  const cellInput: React.CSSProperties = { color: 'var(--ink)', borderBottom: '1px solid var(--line)', padding: '2px 4px' };
  const selectStyle: React.CSSProperties = { ...cellInput, backgroundColor: 'var(--surface-raised)', colorScheme: 'light dark' };
  const optStyle: React.CSSProperties = { backgroundColor: 'var(--surface-raised)', color: 'var(--ink)' };

  return (
    <div className="flex flex-col h-full overflow-hidden" style={{ background: 'var(--surface)' }}>
      {/* Encabezado */}
      <div className="px-5 py-3 shrink-0 flex items-center gap-4 flex-wrap" style={{ borderBottom: '1px solid var(--line)' }}>
        <div className="flex items-center gap-2">
          <ClipboardList size={20} style={{ color: 'var(--emp-accent-txt)' }} />
          <div>
            <h1 className="text-base font-bold" style={{ color: 'var(--ink)' }}>Catálogo de Muestreos</h1>
            <p className="text-[11px]" style={{ color: 'var(--ink-faint)' }}>Labores/muestreos por estadio y sus períodos (días)</p>
          </div>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-xs cursor-pointer select-none" style={{ color: 'var(--ink-muted)' }}>
            <input type="checkbox" checked={ocultarInactivos} onChange={(e) => setOcultarInactivos(e.target.checked)} />
            Ocultar inactivos
          </label>
          <button onClick={() => setAbiertos(new Set(estadios.map((e) => e.codigo)))}
            className="px-3 py-2 rounded text-xs font-medium"
            style={{ background: 'var(--surface-deep)', border: '1px solid var(--line)', color: 'var(--ink-muted)' }}>
            Expandir todos
          </button>
          <button onClick={() => setAbiertos(new Set())}
            className="px-3 py-2 rounded text-xs font-medium"
            style={{ background: 'var(--surface-deep)', border: '1px solid var(--line)', color: 'var(--ink-muted)' }}>
            Contraer todos
          </button>
          <button onClick={cargar} disabled={loading}
            className="flex items-center gap-1.5 px-3 py-2 rounded text-xs font-medium"
            style={{ background: 'var(--surface-deep)', border: '1px solid var(--line)', color: 'var(--ink-muted)' }}>
            {loading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Recargar
          </button>
          <button onClick={imprimirPdf}
            className="flex items-center gap-1.5 px-3 py-2 rounded text-xs font-medium"
            style={{ background: 'var(--surface-deep)', border: '1px solid var(--line)', color: 'var(--ink-muted)' }}>
            <Printer size={13} /> PDF
          </button>
          <button onClick={() => setWaModal(true)}
            className="flex items-center gap-1.5 px-3 py-2 rounded text-xs font-medium"
            style={{ background: 'var(--surface-deep)', border: '1px solid var(--line)', color: 'var(--ink-muted)' }}>
            <MessageCircle size={13} /> WhatsApp
          </button>
          <button onClick={regenerar} disabled={generando}
            className="flex items-center gap-1.5 px-3 py-2 rounded text-xs font-semibold"
            style={{ background: 'var(--emp-accent)', color: 'var(--emp-accent-on)', opacity: generando ? 0.6 : 1 }}>
            {generando ? <Loader2 size={13} className="animate-spin" /> : <CalendarCheck size={13} />} Regenerar calendario
          </button>
        </div>
      </div>

      {toast && (
        <div className="px-5 py-2 shrink-0 text-xs font-medium"
          style={{ background: toast.ok ? 'var(--tone-success-bg)' : '#fff4e5', color: toast.ok ? 'var(--tone-success-text)' : '#a15c00', borderBottom: `1px solid ${toast.ok ? 'var(--tone-success-border)' : '#ffb74d'}` }}>
          {toast.msg}
        </div>
      )}

      <div className="flex-1 overflow-auto p-5 space-y-3">
        <p className="text-[11px]" style={{ color: 'var(--ink-muted)' }}>
          Cada estadio tiene su propia lista. Editá período (días), clase o nombre y guardá la fila. Tras cambiar labores, tocá <b>Regenerar calendario</b>.
        </p>

        {/* Acordeón por estadio */}
        {estadios.map((est) => {
          const abierto = abiertos.has(est.codigo);
          const grupo = labores
            .filter((l) => l.estadio_codigo === est.codigo)
            .filter((l) => !ocultarInactivos || valFor(l, 'activo'))
            .sort((a, b) => (Number(valFor(a, 'offset_dias')) || 0) - (Number(valFor(b, 'offset_dias')) || 0));
          return (
            <div key={est.codigo} className="rounded-lg overflow-hidden" style={{ border: '1px solid var(--line-dim)' }}>
              {/* Cabecera del acordeón */}
              <button onClick={() => toggle(est.codigo)}
                className="w-full flex items-center gap-2 px-3 py-2.5 text-left"
                style={{ background: 'var(--surface-deep)' }}>
                {abierto ? <ChevronDown size={16} style={{ color: 'var(--emp-accent-txt)' }} /> : <ChevronRight size={16} style={{ color: 'var(--ink-faint)' }} />}
                <span className="text-sm font-bold" style={{ color: 'var(--ink)' }}>{est.codigo}</span>
                <span className="text-xs" style={{ color: 'var(--ink-muted)' }}>{est.descripcion}</span>
                <span className="text-[10px]" style={{ color: 'var(--ink-faint)' }}>· {ANCLA_LABEL[est.varfecha] || est.varfecha}</span>
                <span className="ml-auto text-[11px] px-2 py-0.5 rounded-full"
                  style={{ background: 'var(--surface-raised)', color: 'var(--ink-muted)', border: '1px solid var(--line-dim)' }}>
                  {grupo.length} labores
                </span>
              </button>

              {/* Contenido */}
              {abierto && (
                <div className="px-3 pb-3 pt-1">
                  {grupo.length === 0 ? (
                    <p className="text-[11px] py-2" style={{ color: 'var(--ink-faint)' }}>Sin labores en este estadio. Agregá abajo.</p>
                  ) : (
                    <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid var(--line)', color: 'var(--ink-muted)' }}>
                          <th className="text-left py-1.5 px-2 text-[11px] uppercase tracking-wider" style={{ width: '40%' }}>Labor / Muestreo</th>
                          <th className="text-left py-1.5 px-2 text-[11px] uppercase tracking-wider">Clase</th>
                          <th className="text-left py-1.5 px-2 text-[11px] uppercase tracking-wider">Días</th>
                          <th className="text-left py-1.5 px-2 text-[11px] uppercase tracking-wider">Activo</th>
                          <th className="text-left py-1.5 px-2 text-[11px] uppercase tracking-wider"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {grupo.map((l) => (
                          <tr key={l.id} style={{ borderBottom: '1px solid var(--line-dim)', opacity: valFor(l, 'activo') ? 1 : 0.5 }}>
                            <td className="py-1 px-2">
                              <input className={inputCls} style={{ ...cellInput, width: '100%' }}
                                value={valFor(l, 'nombre')} onChange={(e) => setCampo(l.id, 'nombre', e.target.value)} />
                            </td>
                            <td className="py-1 px-2">
                              <select className={inputCls}
                                style={{ ...selectStyle, color: valFor(l, 'clase') === 'tarea' ? 'var(--tone-warn-text)' : 'var(--emp-accent-txt)', fontWeight: 600 }}
                                value={valFor(l, 'clase') || 'muestreo'} onChange={(e) => setCampo(l.id, 'clase', e.target.value)}>
                                <option value="muestreo" style={optStyle}>Muestreo</option>
                                <option value="tarea" style={optStyle}>Tarea</option>
                              </select>
                            </td>
                            <td className="py-1 px-2">
                              <input type="number" min={0} className={inputCls} style={{ ...cellInput, width: '4.5rem' }}
                                value={valFor(l, 'offset_dias') === 0 || valFor(l, 'offset_dias') == null ? '' : valFor(l, 'offset_dias')}
                                onChange={(e) => setCampo(l.id, 'offset_dias', e.target.value === '' ? 0 : Number(e.target.value))} />
                            </td>
                            <td className="py-1 px-2">
                              <input type="checkbox" checked={!!valFor(l, 'activo')} onChange={(e) => setCampo(l.id, 'activo', e.target.checked)} />
                            </td>
                            <td className="py-1 px-2">
                              <div className="flex items-center gap-1.5">
                                {dirty(l.id) && (
                                  <button onClick={() => guardar(l)} disabled={guardando === l.id}
                                    className="flex items-center gap-1 px-2 py-1 rounded text-[11px] font-semibold"
                                    style={{ background: 'var(--emp-accent)', color: 'var(--emp-accent-on)' }}>
                                    {guardando === l.id ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />} Guardar
                                  </button>
                                )}
                                <button onClick={() => pedirBorrar(l)} title="Eliminar labor (en cascada)"
                                  className="p-1 rounded"
                                  style={{ border: '1px solid var(--line-dim)', color: 'var(--tone-error-text)' }}>
                                  <Trash2 size={13} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* Agregar nueva labor */}
        <div className="mt-4 rounded-lg p-3" style={{ background: 'var(--surface-deep)', border: '1px solid var(--line-dim)' }}>
          <h3 className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: 'var(--ink-muted)' }}>Agregar labor / muestreo</h3>
          <div className="flex items-end gap-3 flex-wrap">
            <div>
              <p className="text-[9px] uppercase tracking-widest" style={{ color: 'var(--ink-faint)' }}>Nombre</p>
              <input className={inputCls} style={{ ...cellInput, width: '14rem' }}
                value={nuevo.nombre || ''} onChange={(e) => setNuevo((n) => ({ ...n, nombre: e.target.value }))} />
            </div>
            <div>
              <p className="text-[9px] uppercase tracking-widest" style={{ color: 'var(--ink-faint)' }}>Estadio</p>
              <select className={inputCls} style={selectStyle}
                value={nuevo.estadio_codigo} onChange={(e) => setNuevo((n) => ({ ...n, estadio_codigo: e.target.value }))}>
                {estadios.map((e) => (
                  <option key={e.codigo} value={e.codigo} style={optStyle}>{e.codigo} — {e.descripcion}</option>
                ))}
              </select>
            </div>
            <div>
              <p className="text-[9px] uppercase tracking-widest" style={{ color: 'var(--ink-faint)' }}>Clase</p>
              <select className={inputCls} style={selectStyle}
                value={nuevo.clase || 'muestreo'} onChange={(e) => setNuevo((n) => ({ ...n, clase: e.target.value as any }))}>
                <option value="muestreo" style={optStyle}>Muestreo</option>
                <option value="tarea" style={optStyle}>Tarea</option>
              </select>
            </div>
            <div>
              <p className="text-[9px] uppercase tracking-widest" style={{ color: 'var(--ink-faint)' }}>Días</p>
              <input type="number" min={0} className={inputCls} style={{ ...cellInput, width: '4.5rem' }}
                value={nuevo.offset_dias ? nuevo.offset_dias : ''} placeholder="0"
                onChange={(e) => setNuevo((n) => ({ ...n, offset_dias: e.target.value === '' ? 0 : Number(e.target.value) }))} />
            </div>
            <button onClick={crear} disabled={guardando === 'nuevo'}
              className="flex items-center gap-1.5 px-3 py-2 rounded text-xs font-semibold"
              style={{ background: 'var(--emp-accent)', color: 'var(--emp-accent-on)' }}>
              {guardando === 'nuevo' ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Agregar
            </button>
          </div>
        </div>
      </div>

      {/* Modal confirmación de borrado en cascada */}
      {borrar && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4"
          style={{ background: 'rgba(8,12,22,0.72)' }} onClick={() => !borrando && setBorrar(null)}>
          <div className="rounded-xl w-full max-w-sm overflow-hidden"
            style={{ background: 'var(--surface-base)', border: '1px solid var(--tone-error-border)', boxShadow: '0 24px 60px rgba(0,0,0,0.55)' }}
            onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 flex items-center gap-2" style={{ borderBottom: '1px solid var(--line)' }}>
              <Trash2 size={18} style={{ color: 'var(--tone-error-text)' }} />
              <h2 className="text-sm font-bold flex-1" style={{ color: 'var(--ink)' }}>Eliminar labor</h2>
              <button onClick={() => !borrando && setBorrar(null)} className="p-1 rounded" style={{ color: 'var(--ink-faint)' }}><X size={18} /></button>
            </div>
            <div className="px-5 py-4 space-y-2">
              <p className="text-sm" style={{ color: 'var(--ink)' }}>
                ¿Eliminar <b>{borrar.labor.nombre}</b> <span className="font-mono text-xs">[{borrar.labor.estadio_codigo}]</span>?
              </p>
              <p className="text-[12px]" style={{ color: 'var(--ink-muted)' }}>
                Se borrarán también <b>{borrar.programados}</b> muestreo(s) del calendario
                {borrar.ejecutados > 0 && (
                  <> — incluidos <b style={{ color: 'var(--tone-error-text)' }}>{borrar.ejecutados} ya ejecutado(s) con datos capturados</b></>
                )}.
              </p>
              <p className="text-[11px]" style={{ color: 'var(--ink-faint)' }}>Esta acción no se puede deshacer.</p>
            </div>
            <div className="px-5 py-3 flex items-center justify-end gap-2" style={{ borderTop: '1px solid var(--line)' }}>
              <button onClick={() => setBorrar(null)} disabled={borrando}
                className="px-3 py-2 rounded text-xs font-medium" style={{ border: '1px solid var(--line)', color: 'var(--ink-muted)' }}>Cancelar</button>
              <button onClick={confirmarBorrar} disabled={borrando}
                className="flex items-center gap-1.5 px-4 py-2 rounded text-xs font-semibold"
                style={{ background: 'var(--tone-error-text)', color: '#fff', opacity: borrando ? 0.6 : 1 }}>
                {borrando ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />} Eliminar
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal envío por WhatsApp */}
      {waModal && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4"
          style={{ background: 'rgba(8,12,22,0.72)' }} onClick={() => !waEnviando && setWaModal(false)}>
          <div className="rounded-xl w-full max-w-sm overflow-hidden"
            style={{ background: 'var(--surface-base)', border: '1px solid var(--line)', boxShadow: '0 24px 60px rgba(0,0,0,0.55)' }}
            onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 flex items-center gap-2" style={{ borderBottom: '1px solid var(--line)' }}>
              <MessageCircle size={18} style={{ color: 'var(--tone-success-text)' }} />
              <h2 className="text-sm font-bold flex-1" style={{ color: 'var(--ink)' }}>Enviar catálogo por WhatsApp</h2>
              <button onClick={() => !waEnviando && setWaModal(false)} className="p-1 rounded" style={{ color: 'var(--ink-faint)' }}><X size={18} /></button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <p className="text-[12px]" style={{ color: 'var(--ink-muted)' }}>
                Se enviará el reporte del catálogo (por estadio, {ocultarInactivos ? 'solo activas' : 'todas'}) como imagen.
              </p>
              <div>
                <p className="text-[9px] uppercase tracking-widest mb-1" style={{ color: 'var(--ink-faint)' }}>Número de WhatsApp</p>
                <input className={inputCls} style={{ ...cellInput, width: '100%' }} placeholder="Ej: 50688887777"
                  value={waNumero} onChange={(e) => setWaNumero(e.target.value)} disabled={waEnviando} autoFocus />
              </div>
            </div>
            <div className="px-5 py-3 flex items-center justify-end gap-2" style={{ borderTop: '1px solid var(--line)' }}>
              <button onClick={() => setWaModal(false)} disabled={waEnviando}
                className="px-3 py-2 rounded text-xs font-medium" style={{ border: '1px solid var(--line)', color: 'var(--ink-muted)' }}>Cancelar</button>
              <button onClick={enviarWhatsApp} disabled={waEnviando}
                className="flex items-center gap-1.5 px-4 py-2 rounded text-xs font-semibold"
                style={{ background: 'var(--tone-success-text)', color: '#fff', opacity: waEnviando ? 0.6 : 1 }}>
                {waEnviando ? <Loader2 size={13} className="animate-spin" /> : <MessageCircle size={13} />} Enviar
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
